import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { Search, X, Plus, ArrowLeftRight, Printer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { r2Url } from '@/lib/r2';
import { cn } from '@/lib/utils';
import { APMBadge } from '@/components/APMBadge';

// ── Category classification ─────────────────────────────────────────────────
import { getComponentSortRank } from '@/types/database';
const categoryRank = (cat: string) => getComponentSortRank(cat ?? '');

const canonicalCategory = (cat: string): string => {
  const lower = cat?.toLowerCase() ?? '';
  if (['cpu cooler', 'fan'].includes(lower)) return 'cpu cooler';
  if (['storage', 'rom'].includes(lower)) return 'storage';
  if (['case', 'cas'].includes(lower)) return 'case';
  if (['accessories', 'acc'].includes(lower)) return 'accessories';
  return lower;
};

const CATEGORY_LABELS: Record<string, string> = {
  cpu: 'CPU', 'cpu cooler': 'CPU Cooler', mb: 'Motherboard', ram: 'RAM',
  storage: 'Storage', gpu: 'GPU', psu: 'PSU', os: 'Operating System',
  case: 'Case', accessories: 'Accessories',
};

const MAJOR_CATEGORIES = new Set(['cpu', 'ram', 'storage', 'gpu', 'case']);
const MINOR_CATEGORIES = new Set(['mb', 'psu', 'os', 'accessories', 'cpu cooler']);

const MAX_BUILDS = 4;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-MT', { style: 'currency', currency: 'EUR' }).format(val);

// Round to nearest number ending in 9 (e.g. 1047 → 1049, 1052 → 1049, 1055 → 1059)
import { EPS_PLANS, calcEps, calcRrp } from '@/lib/pricing';

interface BuildOption {
  id: string;
  sku: string;
  description: string;
  status: string;
  rrp: number | null;
}

interface BomItem {
  component_sku: string;
  component_type: string;
  qty_per_build: number;
  cost_ex_vat: number;
  description?: string;
  category?: string;
}

interface MediaItem {
  url: string;
  source?: string;
}

interface PrintOptions {
  codes: boolean;
  status: boolean;
  price: boolean;
}

type PrintOrientation = 'landscape' | 'portrait';

const COMPARE_SESSION_KEY = 'compare-session';

interface CompareSession {
  selectedIds: string[];
  showApm: boolean;
  showEps: boolean;
  showSpecs: boolean;
}

function loadCompareSession(): Partial<CompareSession> {
  try {
    const raw = localStorage.getItem(COMPARE_SESSION_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<CompareSession>;
  } catch {
    return {};
  }
}

export function ComparePage() {
  const _session = loadCompareSession();
  const [selectedIds, setSelectedIds] = useState<string[]>(_session.selectedIds ?? []);
  const [searchInputs, setSearchInputs] = useState<Record<number, string>>({});
  const [openPicker, setOpenPicker] = useState<number | null>(null);
  const [customDescriptions, setCustomDescriptions] = useState<Record<string, string>>({});
  const [customCompDescs, setCustomCompDescs] = useState<Record<string, string>>({});
  const [printOptions, setPrintOptions] = useState<PrintOptions>({ codes: true, status: true, price: false });
  const [showApm, setShowApm] = useState(_session.showApm ?? true);
  const [showApmParams, setShowApmParams] = useState(false);
  const [showEps, setShowEps] = useState(_session.showEps ?? false);
  const [showSpecs, setShowSpecs] = useState(_session.showSpecs ?? false);
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  const [hiddenComponents, setHiddenComponents] = useState<Set<string>>(new Set()); // "buildId:sku" keys
  const printRef = useRef<HTMLDivElement>(null);

  // Persist session to localStorage whenever key state changes
  useEffect(() => {
    const session: CompareSession = { selectedIds, showApm, showEps, showSpecs };
    localStorage.setItem(COMPARE_SESSION_KEY, JSON.stringify(session));
  }, [selectedIds, showApm, showEps, showSpecs]);

  // Get the display description: custom override or original
  const getDescription = (buildId: string, original: string) =>
    customDescriptions[buildId] !== undefined ? customDescriptions[buildId] : original;

  // Get component description: custom override or original
  const getCompDesc = (buildId: string, sku: string, original: string) => {
    const key = `${buildId}:${sku}`;
    return customCompDescs[key] !== undefined ? customCompDescs[key] : original;
  };
  const setCompDesc = (buildId: string, sku: string, value: string) => {
    setCustomCompDescs(prev => ({ ...prev, [`${buildId}:${sku}`]: value }));
  };

  const handlePrint = useCallback((orientation: PrintOrientation) => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Build Comparison</title>
  <style>
    @page { size: ${orientation}; margin: 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 11px; color: #111; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 6px 8px; border: 1px solid #ddd; text-align: left; vertical-align: top; }
    thead th { background: #f5f5f5; font-weight: 600; }
    .build-header { text-align: center; }
    .build-header img { max-width: 120px; max-height: 120px; object-fit: cover; border-radius: 6px; margin: 0 auto 6px; display: block; }
    .build-sku { font-family: monospace; font-weight: 700; font-size: 12px; }
    .build-desc { font-size: 10px; color: #666; }
    .build-status { display: inline-block; font-size: 9px; padding: 1px 6px; border: 1px solid #ccc; border-radius: 9999px; margin-top: 2px; text-transform: capitalize; }
    .cat-label { font-weight: 600; white-space: nowrap; min-width: 90px; }
    .comp-desc { font-size: 11px; }
    .comp-sku { font-family: monospace; font-size: 9px; color: #888; }
    .comp-qty { font-size: 9px; color: #888; }
    .comp-price { font-size: 9px; color: #666; font-weight: 500; }
    .diff-major { background: #fffbeb; border-left: 3px solid #f59e0b; }
    .diff-major .cat-label { color: #92400e; }
    .diff-minor { background: #eff6ff; border-left: 3px solid #93c5fd; }
    .diff-minor .cat-label { color: #1e3a5f; }
    .dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-left: 4px; vertical-align: middle; }
    .dot-major { background: #f59e0b; }
    .dot-minor { background: #93c5fd; }
    .legend { margin-top: 12px; font-size: 10px; color: #666; display: flex; gap: 16px; }
    .legend-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; vertical-align: middle; }
    .no-image { width: 120px; height: 120px; background: #f3f4f6; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin: 0 auto 6px; font-size: 10px; color: #999; }
    .spec-line { font-size: 9px; color: #6b7280; margin-top: 1px; }
    .em-dash { color: #ccc; }
    .print-title { font-size: 18px; font-weight: 700; margin-bottom: 12px; }
    .total-row { font-weight: 600; border-top: 2px solid #999; }
  </style>
</head>
<body>
  <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px;">
    <img src="/logo-horizontal.png" alt="AOI" style="height:40px;width:auto;" />
    <div class="print-title" style="margin-bottom:0;">Build Comparison</div>
  </div>
  ${printContent}
</body>
</html>`);
    printWindow.document.close();

    const images = printWindow.document.querySelectorAll('img');
    let loaded = 0;
    const total = images.length;
    let printed = false;
    const doPrint = () => { if (printed) return; printed = true; printWindow.focus(); printWindow.print(); };
    if (total === 0) {
      setTimeout(doPrint, 100);
    } else {
      images.forEach(img => {
        img.onload = img.onerror = () => { loaded++; if (loaded >= total) setTimeout(doPrint, 100); };
      });
      setTimeout(doPrint, 3000);
    }
  }, []);

  // ── Data queries ──────────────────────────────────────────────────────────

  const { data: allBuilds = [] } = useQuery({
    queryKey: ['compare-builds-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('builds')
        .select('id, sku, description, status, rrp')
        .order('sku');
      if (error) throw error;
      return (data ?? []) as BuildOption[];
    },
  });

  const { data: bomData = {} } = useQuery({
    queryKey: ['compare-bom', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return {};
      const selectedSkus: Record<string, string> = {};
      selectedIds.forEach(id => {
        const b = allBuilds.find(x => x.id === id);
        if (b) selectedSkus[id] = b.sku;
      });

      const { data: bomRows } = await supabase
        .from('build_components')
        .select('build_id, component_sku, component_type, qty_per_build, cost_ex_vat')
        .in('build_id', selectedIds);

      const buildsWithBom = new Set((bomRows ?? []).map(r => r.build_id));
      const buildsNeedFallback = selectedIds.filter(id => !buildsWithBom.has(id));

      let fallbackComponents: any[] = [];
      if (buildsNeedFallback.length > 0) {
        const skusToSearch = buildsNeedFallback.map(id => selectedSkus[id]).filter(Boolean);
        if (skusToSearch.length > 0) {
          const orFilter = skusToSearch.map(sku => `build_sku.ilike.%${sku}%`).join(',');
          const { data: fbData } = await supabase
            .from('components')
            .select('part_number, description, category, current_price, build_sku')
            .or(orFilter);
          fallbackComponents = fbData ?? [];
        }
      }

      const bcSkus = [...new Set((bomRows ?? []).map(r => r.component_sku).filter(Boolean))];
      const descMap: Record<string, { description: string; category: string; price: number }> = {};
      if (bcSkus.length > 0) {
        const { data: compData } = await supabase
          .from('components')
          .select('part_number, description, category, current_price')
          .in('part_number', bcSkus);
        (compData ?? []).forEach((c: any) => {
          descMap[c.part_number] = { description: c.description || '', category: c.category || '', price: c.current_price ?? 0 };
        });
      }

      const result: Record<string, BomItem[]> = {};
      (bomRows ?? []).forEach((r: any) => {
        if (!result[r.build_id]) result[r.build_id] = [];
        result[r.build_id].push({
          ...r,
          cost_ex_vat: r.cost_ex_vat || descMap[r.component_sku]?.price || 0,
          description: descMap[r.component_sku]?.description || '',
          category: descMap[r.component_sku]?.category || r.component_type || '',
        });
      });

      for (const buildId of buildsNeedFallback) {
        const sku = selectedSkus[buildId];
        if (!sku) continue;
        const matched = fallbackComponents.filter(c => {
          const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
          return entries.includes(sku);
        });
        result[buildId] = matched.map(c => ({
          build_id: buildId,
          component_sku: c.part_number,
          component_type: c.category || '',
          qty_per_build: 1,
          cost_ex_vat: c.current_price ?? 0,
          description: c.description || '',
          category: c.category || '',
        }));
      }

      Object.values(result).forEach(items =>
        items.sort((a, b) => categoryRank(a.category || '') - categoryRank(b.category || ''))
      );
      return result;
    },
    enabled: selectedIds.length > 0 && allBuilds.length > 0,
  });

  const { data: mediaData = {} } = useQuery({
    queryKey: ['compare-media', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return {};
      const result: Record<string, MediaItem | null> = {};
      const { data: buildMedia } = await supabase
        .from('build_media')
        .select('build_id, file_path')
        .in('build_id', selectedIds)
        .order('created_at', { ascending: true });

      const covered = new Set<string>();
      (buildMedia ?? []).forEach((m: any) => {
        if (!covered.has(m.build_id)) {
          covered.add(m.build_id);
          result[m.build_id] = { url: r2Url(`build-media/${m.file_path}`) };
        }
      });

      const uncovered = selectedIds.filter(id => !covered.has(id));
      if (uncovered.length > 0 && Object.keys(bomData).length > 0) {
        for (const buildId of uncovered) {
          const bom = bomData[buildId] ?? [];
          const caseItem = bom.find(b => ['case', 'cas'].includes(b.category?.toLowerCase() ?? ''));
          if (caseItem) {
            const { data: comp } = await supabase.from('components').select('id').eq('part_number', caseItem.component_sku).single();
            if (comp) {
              const { data: cMedia } = await supabase.from('component_media').select('file_path').eq('component_id', comp.id).order('created_at', { ascending: true }).limit(1);
              if (cMedia && cMedia.length > 0) {
                result[buildId] = { url: r2Url(`build-media/${cMedia[0].file_path}`), source: 'case' };
              }
            }
          }
        }
      }
      return result;
    },
    enabled: selectedIds.length > 0 && Object.keys(bomData).length > 0,
  });

  // ── APM scores ──────────────────────────────────────────────────────────
  const { data: apmData = {} } = useQuery({
    queryKey: ['compare-apm', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return {};
      const result: Record<string, { passmark_rating: number | null; cpu_mark: number | null; graphics_3d_mark: number | null; memory_mark: number | null; disk_mark: number | null; sample_count: number } | null> = {};

      // Try live scores from assembly_order_benchmarks
      const { data: orders } = await supabase
        .from('assembly_orders')
        .select('id, build_id')
        .in('build_id', selectedIds);

      if (orders && orders.length > 0) {
        const ordersByBuild: Record<string, string[]> = {};
        orders.forEach((o: any) => {
          if (!ordersByBuild[o.build_id]) ordersByBuild[o.build_id] = [];
          ordersByBuild[o.build_id].push(o.id);
        });

        const allOrderIds = orders.map(o => o.id);
        const { data: benchmarks } = await supabase
          .from('assembly_order_benchmarks')
          .select('assembly_order_id, passmark_rating, cpu_mark, graphics_3d_mark, memory_mark, disk_mark')
          .in('assembly_order_id', allOrderIds);

        for (const buildId of selectedIds) {
          const bOrderIds = new Set(ordersByBuild[buildId] ?? []);
          const bBenchmarks = (benchmarks ?? []).filter((b: any) => bOrderIds.has(b.assembly_order_id));
          if (bBenchmarks.length > 0) {
            const avg = (key: string) => {
              const vals = bBenchmarks.map((b: any) => b[key]).filter((v: any) => v != null && v > 0);
              return vals.length > 0 ? Math.round(vals.reduce((s: number, v: number) => s + v, 0) / vals.length) : null;
            };
            result[buildId] = {
              passmark_rating: avg('passmark_rating'),
              cpu_mark: avg('cpu_mark'),
              graphics_3d_mark: avg('graphics_3d_mark'),
              memory_mark: avg('memory_mark'),
              disk_mark: avg('disk_mark'),
              sample_count: bBenchmarks.length,
            };
          }
        }
      }

      // Fall back to historical for builds without live data
      const missingIds = selectedIds.filter(id => !result[id]);
      if (missingIds.length > 0) {
        const { data: historical } = await supabase
          .from('build_benchmarks')
          .select('build_id, passmark_rating, cpu_mark, graphics_3d_mark, memory_mark, disk_mark, sample_count')
          .in('build_id', missingIds);
        (historical ?? []).forEach((h: any) => {
          result[h.build_id] = h;
        });
      }

      return result;
    },
    enabled: selectedIds.length > 0,
  });

  // ── Component specs ──────────────────────────────────────────────────────
  const allComponentSkus = useMemo(() => {
    const skus = new Set<string>();
    selectedIds.forEach(id => (bomData[id] ?? []).forEach(item => skus.add(item.component_sku)));
    return [...skus];
  }, [selectedIds, bomData]);

  // ── B2C display names ──────────────────────────────────────────────────────
  const { data: b2cNames = {} } = useQuery({
    queryKey: ['compare-display-names', allComponentSkus],
    queryFn: async () => {
      if (allComponentSkus.length === 0) return {};
      const { data } = await supabase
        .from('component_display_names')
        .select('part_number, display_name_b2c')
        .in('part_number', allComponentSkus);
      const map: Record<string, string> = {};
      (data ?? []).forEach((r: any) => {
        if (r.display_name_b2c) map[r.part_number] = r.display_name_b2c;
      });
      return map;
    },
    enabled: allComponentSkus.length > 0,
  });

  // Seed custom component descriptions from B2C display names when loaded
  useEffect(() => {
    if (Object.keys(b2cNames).length === 0) return;
    setCustomCompDescs(prev => {
      const next = { ...prev };
      let changed = false;
      for (const id of selectedIds) {
        for (const item of bomData[id] ?? []) {
          const key = `${id}:${item.component_sku}`;
          if (!(key in next) && b2cNames[item.component_sku]) {
            next[key] = b2cNames[item.component_sku];
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [b2cNames, selectedIds, bomData]);

  const { data: componentSpecsData = {} } = useQuery({
    queryKey: ['compare-component-specs', allComponentSkus],
    queryFn: async () => {
      if (allComponentSkus.length === 0) return {};
      const { data } = await supabase
        .from('components')
        .select('part_number, category, specs')
        .in('part_number', allComponentSkus);
      const map: Record<string, { category: string; specs: Record<string, any> }> = {};
      (data ?? []).forEach((c: any) => {
        map[c.part_number] = { category: c.category || '', specs: c.specs || {} };
      });
      return map;
    },
    enabled: allComponentSkus.length > 0,
  });

  const { data: specsSchemaMap = {} } = useQuery({
    queryKey: ['component-types-schema-compare'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_types')
        .select('code, specs_schema');
      const map: Record<string, any[]> = {};
      (data ?? []).forEach((t: any) => {
        map[t.code] = t.specs_schema?.fields ?? [];
      });
      return map;
    },
  });

  const COMPARE_CAT_TO_TYPE: Record<string, string> = {
    cpu: 'CPU', 'cpu cooler': 'FAN', fan: 'FAN', coo: 'COO',
    mb: 'MB', ram: 'RAM', storage: 'STORAGE', rom: 'STORAGE',
    gpu: 'GPU', psu: 'PSU', case: 'CASE', cas: 'CASE',
    os: 'OS', acc: 'ACC', accessories: 'ACC',
  };

  interface SpecEntry { text: string; missing: boolean }

  const getSpecEntries = useCallback((sku: string): { entries: SpecEntry[]; allMissing: boolean } => {
    const compData = componentSpecsData[sku];
    if (!compData) return { entries: [], allMissing: false };
    const typeCode = COMPARE_CAT_TO_TYPE[compData.category.toLowerCase()] || compData.category.toUpperCase();
    const schema = specsSchemaMap[typeCode] ?? [];
    if (schema.length === 0) return { entries: [], allMissing: false };
    const hasAnySpecs = compData.specs && Object.keys(compData.specs).length > 0;

    const entries = schema.map((f: any) => {
      const val = compData.specs?.[f.key];
      const isMissing = val == null || val === '';
      if (isMissing) return { text: `${f.label}: —`, missing: true };
      if (typeof val === 'boolean') return { text: `${f.label}: ${val ? 'Yes' : 'No'}`, missing: false };
      if (Array.isArray(val)) return { text: `${f.label}: ${val.join(', ')}`, missing: false };
      if (f.unit) return { text: `${val} ${f.unit}`, missing: false };
      return { text: String(val), missing: false };
    });
    return { entries, allMissing: !hasAnySpecs };
  }, [componentSpecsData, specsSchemaMap]);

  const hasAnyApm = selectedIds.some(id => apmData[id]?.passmark_rating != null);

  // ── Comparison grid ───────────────────────────────────────────────────────

  const comparisonRows = useMemo(() => {
    if (selectedIds.length < 2) return [];
    const allCategories = new Set<string>();
    selectedIds.forEach(id => {
      (bomData[id] ?? []).forEach(item => {
        if (!hiddenComponents.has(`${id}:${item.component_sku}`)) {
          allCategories.add(canonicalCategory(item.category || item.component_type || ''));
        }
      });
    });
    const sorted = [...allCategories].sort((a, b) => categoryRank(a) - categoryRank(b));
    return sorted.map(cat => {
      const label = CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
      const isMajor = MAJOR_CATEGORIES.has(cat);
      const isMinor = MINOR_CATEGORIES.has(cat);
      const columns = selectedIds.map(id =>
        (bomData[id] ?? []).filter(item => canonicalCategory(item.category || item.component_type || '') === cat)
      );
      // Visible-only items for difference detection (hidden/unticked components are ignored)
      const visibleColumns = selectedIds.map(id =>
        (bomData[id] ?? []).filter(item =>
          canonicalCategory(item.category || item.component_type || '') === cat &&
          !hiddenComponents.has(`${id}:${item.component_sku}`)
        )
      );
      // Compare by display description (custom rename > b2c name > base description > SKU)
      const signatures = visibleColumns.map((items, colIdx) =>
        items.map(i => {
          const buildId = selectedIds[colIdx];
          const desc = getCompDesc(buildId, i.component_sku, i.description || '') || i.component_sku;
          return `${desc.toLowerCase().trim()}×${i.qty_per_build}`;
        }).sort().join('|')
      );
      const hasDifference = new Set(signatures).size > 1;
      return { cat, label, isMajor, isMinor, columns, hasDifference };
    });
  }, [selectedIds, bomData, hiddenComponents, customCompDescs]);

  // Per-build total cost
  const buildTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    selectedIds.forEach(id => {
      totals[id] = (bomData[id] ?? []).reduce((sum, item) => sum + (item.cost_ex_vat ?? 0) * (item.qty_per_build ?? 1), 0);
    });
    return totals;
  }, [selectedIds, bomData]);

  // Per-component visibility helpers
  const isCompVisible = (buildId: string, sku: string) => !hiddenComponents.has(`${buildId}:${sku}`);
  const toggleCompVisible = (buildId: string, sku: string) => {
    setHiddenComponents(prev => {
      const next = new Set(prev);
      const key = `${buildId}:${sku}`;
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Check if a category has multiple different components across any build
  const categoryHasMultiple = useMemo(() => {
    const result: Record<string, boolean> = {};
    comparisonRows.forEach(row => {
      result[row.cat] = row.columns.some(items => items.length > 1);
    });
    return result;
  }, [comparisonRows]);

  const addBuild = (id: string) => {
    if (selectedIds.includes(id) || selectedIds.length >= MAX_BUILDS) return;
    setSelectedIds(prev => [...prev, id]);
    setSearchInputs(prev => ({ ...prev, [-1]: '' }));
    setOpenPicker(null);
  };
  const removeBuild = (index: number) => {
    setSelectedIds(prev => prev.filter((_, i) => i !== index));
  };

  const selectedBuilds = selectedIds.map(id => allBuilds.find(b => b.id === id)).filter(Boolean) as BuildOption[];

  // ── Toggle helper ─────────────────────────────────────────────────────────
  const PrintToggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="rounded border-gray-300" />
      {label}
    </label>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="h-7 w-7" />
            Compare Builds
          </h1>
          <p className="text-muted-foreground">Select 2–{MAX_BUILDS} builds to compare side by side</p>
        </div>
        {selectedIds.length >= 2 && (
          <div className="flex items-center gap-4">
            {/* View toggles */}
            <div className="flex items-center gap-3 border-r pr-4">
              {hasAnyApm && <PrintToggle label="APM" checked={showApm} onChange={setShowApm} />}
              {hasAnyApm && <PrintToggle label="APM Parameters" checked={showApmParams} onChange={setShowApmParams} />}
              <PrintToggle label="EPS" checked={showEps} onChange={setShowEps} />
              <PrintToggle label="Specs" checked={showSpecs} onChange={setShowSpecs} />
            </div>
            {/* Print option toggles */}
            <div className="flex items-center gap-3 border-r pr-4">
              <span className="text-xs text-muted-foreground font-medium">Print:</span>
              <PrintToggle label="Codes" checked={printOptions.codes} onChange={v => setPrintOptions(p => ({ ...p, codes: v }))} />
              <PrintToggle label="Status" checked={printOptions.status} onChange={v => setPrintOptions(p => ({ ...p, status: v }))} />
              <PrintToggle label="Price" checked={printOptions.price} onChange={v => setPrintOptions(p => ({ ...p, price: v }))} />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePrint('portrait')} className="gap-1.5">
                <Printer className="h-4 w-4" /> Portrait
              </Button>
              <Button variant="outline" size="sm" onClick={() => handlePrint('landscape')} className="gap-1.5">
                <Printer className="h-4 w-4" /> Landscape
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Build picker */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 items-start">
            {selectedIds.map((id, index) => {
              const build = allBuilds.find(b => b.id === id);
              return (
                <div key={id} className="flex items-center gap-1 bg-primary/10 rounded-lg px-3 py-2">
                  <span className="font-mono text-sm font-medium">{build?.sku}</span>
                  <button onClick={() => removeBuild(index)} className="ml-1 hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            {selectedIds.length < MAX_BUILDS && (
              <div className="relative">
                <Button variant="outline" size="sm" onClick={() => setOpenPicker(openPicker === -1 ? null : -1)} className="gap-1">
                  <Plus className="h-4 w-4" /> Add Build
                </Button>
                {openPicker === -1 && (
                  <div className="absolute top-full left-0 mt-1 z-50 w-80 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by SKU or description..."
                          className="pl-8 h-9 text-sm"
                          value={searchInputs[-1] || ''}
                          onChange={e => setSearchInputs(prev => ({ ...prev, [-1]: e.target.value }))}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-56">
                      {allBuilds
                        .filter(b => {
                          if (selectedIds.includes(b.id)) return false;
                          const q = (searchInputs[-1] || '').toLowerCase();
                          if (!q) return true;
                          return b.sku.toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                        })
                        .slice(0, 50)
                        .map(b => (
                          <button key={b.id} className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm border-b last:border-b-0" onClick={() => addBuild(b.id)}>
                            <span className="font-mono font-medium text-primary">{b.sku}</span>
                            <span className="text-muted-foreground ml-2 truncate">{b.description}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {openPicker !== null && <div className="fixed inset-0 z-40" onClick={() => setOpenPicker(null)} />}
        </CardContent>
      </Card>

      {/* Category toggles */}
      {selectedIds.length >= 2 && comparisonRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground mr-1">Show:</span>
          {comparisonRows.map(row => {
            const isHidden = hiddenCategories.has(row.cat);
            return (
              <button
                key={row.cat}
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full border transition-colors',
                  isHidden ? 'bg-muted text-muted-foreground border-muted' : 'bg-primary/10 text-primary border-primary/20',
                )}
                onClick={() => setHiddenCategories(prev => {
                  const next = new Set(prev);
                  if (next.has(row.cat)) next.delete(row.cat); else next.add(row.cat);
                  return next;
                })}
              >
                {row.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Comparison table */}
      {selectedIds.length >= 2 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="p-3 text-left font-medium bg-muted/50 w-36 sticky left-0 z-10">Build</th>
                    {selectedBuilds.map(build => (
                      <th key={build.id} className="p-3 text-center min-w-[220px]">
                        <div className="space-y-2">
                          {mediaData[build.id] ? (
                            <div className="mx-auto w-40 h-40 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                              <img src={mediaData[build.id]!.url} alt={build.sku} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="mx-auto w-40 h-40 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-muted-foreground text-xs">
                              No image
                            </div>
                          )}
                          <div>
                            <p className="font-mono font-bold text-primary">{build.sku}</p>
                            {/* Editable description */}
                            <input
                              className="text-xs text-muted-foreground text-center w-full max-w-[200px] mx-auto bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none px-1 py-0.5 transition-colors"
                              value={getDescription(build.id, build.description)}
                              onChange={e => setCustomDescriptions(prev => ({ ...prev, [build.id]: e.target.value }))}
                              title="Click to edit description for print"
                            />
                            <Badge variant="outline" className="mt-1 text-xs capitalize">{build.status}</Badge>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* APM row */}
                  {showApm && hasAnyApm && (
                    <tr className="border-b bg-muted/30">
                      <td className="p-3 font-medium whitespace-nowrap sticky left-0 z-10 bg-muted/30">APM Score</td>
                      {selectedBuilds.map(build => {
                        const apm = apmData[build.id];
                        return (
                          <td key={build.id} className="p-3 text-center">
                            {apm?.passmark_rating != null ? (
                              <APMBadge passmarkRating={apm.passmark_rating} compact />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {/* APM parameter rows */}
                  {showApmParams && hasAnyApm && (
                    <>
                      {([
                        { key: 'cpu_mark', label: 'CPU Mark' },
                        { key: 'graphics_3d_mark', label: '3D Graphics' },
                        { key: 'memory_mark', label: 'Memory' },
                        { key: 'disk_mark', label: 'Disk' },
                      ] as { key: keyof typeof apmData[string] & string; label: string }[]).map(({ key, label }) => (
                        <tr key={key} className="border-b bg-muted/20">
                          <td className="p-3 text-sm text-muted-foreground whitespace-nowrap sticky left-0 z-10 bg-muted/20 pl-6">{label}</td>
                          {selectedBuilds.map(build => {
                            const apm = apmData[build.id];
                            const val = apm?.[key as keyof typeof apm] as number | null | undefined;
                            return (
                              <td key={build.id} className="p-3 text-center text-sm">
                                {val != null ? val.toLocaleString() : <span className="text-xs text-muted-foreground">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </>
                  )}
                  {comparisonRows.filter(row => !hiddenCategories.has(row.cat)).map(row => (
                    <tr
                      key={row.cat}
                      className={cn(
                        'border-b transition-colors',
                        row.hasDifference && row.isMajor && 'bg-amber-50 dark:bg-amber-950/40 border-l-4 border-l-amber-400',
                        row.hasDifference && row.isMinor && 'bg-blue-50/50 dark:bg-blue-950/30 border-l-4 border-l-blue-300 dark:border-l-blue-600',
                        row.hasDifference && !row.isMajor && !row.isMinor && 'bg-gray-50 dark:bg-gray-800/50',
                        !row.hasDifference && 'hover:bg-muted/30',
                      )}
                    >
                      <td className={cn(
                        'p-3 font-medium whitespace-nowrap sticky left-0 z-10',
                        row.hasDifference && row.isMajor && 'bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-300',
                        row.hasDifference && row.isMinor && 'bg-blue-50/50 dark:bg-blue-950/30 text-blue-900 dark:text-blue-300',
                        !row.hasDifference && 'bg-white dark:bg-gray-900',
                        row.hasDifference && !row.isMajor && !row.isMinor && 'bg-gray-50 dark:bg-gray-800/50',
                      )}>
                        <div className="flex items-center gap-1.5">
                          {row.label}
                          {row.hasDifference && row.isMajor && <span className="inline-block w-2 h-2 rounded-full bg-amber-400" title="Major difference" />}
                          {row.hasDifference && row.isMinor && <span className="inline-block w-2 h-2 rounded-full bg-blue-400" title="Minor difference" />}
                        </div>
                      </td>
                      {row.columns.map((items, colIdx) => (
                        <td key={selectedIds[colIdx]} className="p-3 align-top">
                          {items.length > 0 ? (
                            <div className="space-y-1">
                              {items.map((item, itemIdx) => {
                                const buildId = selectedIds[colIdx];
                                const visible = isCompVisible(buildId, item.component_sku);
                                const showToggle = categoryHasMultiple[row.cat];
                                return (
                                  <div key={itemIdx} className={cn(!visible && 'opacity-40')}>
                                    <div className="flex items-start gap-1.5">
                                      {showToggle && (
                                        <button
                                          onClick={() => toggleCompVisible(buildId, item.component_sku)}
                                          className="mt-1 text-muted-foreground hover:text-foreground shrink-0"
                                          title={visible ? 'Hide from print' : 'Show in print'}
                                        >
                                          {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                                        </button>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <input
                                          className="text-sm w-full bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none px-0 py-0.5 transition-colors"
                                          value={getCompDesc(buildId, item.component_sku, item.description || item.component_sku)}
                                          onChange={e => setCompDesc(buildId, item.component_sku, e.target.value)}
                                          title="Click to edit description for print"
                                        />
                                        <p className="text-xs text-muted-foreground font-mono">{item.component_sku}</p>
                                        {item.qty_per_build > 1 && <p className="text-xs text-muted-foreground">Qty: {item.qty_per_build}</p>}
                                        {showSpecs && (() => {
                                          const { entries, allMissing } = getSpecEntries(item.component_sku);
                                          if (entries.length === 0) return null;
                                          if (allMissing) {
                                            return (
                                              <div className="text-[11px] mt-0.5 leading-tight text-orange-500 flex items-center gap-1">
                                                <span>⚠</span>
                                                <span>No specs</span>
                                              </div>
                                            );
                                          }
                                          return (
                                            <div className="text-[11px] mt-0.5 leading-tight flex flex-wrap gap-x-1">
                                              {entries.map((e, idx) => (
                                                <span key={idx}>
                                                  <span className={e.missing ? 'text-orange-500' : 'text-muted-foreground'}>
                                                    {e.text}
                                                  </span>
                                                  {idx < entries.length - 1 && <span className="text-muted-foreground/50"> · </span>}
                                                </span>
                                              ))}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* RRP row */}
                  {comparisonRows.length > 0 && (
                    <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-primary/5 font-bold">
                      <td className="p-3 sticky left-0 z-10 bg-primary/5">
                        RRP (inc. VAT)
                      </td>
                      {selectedBuilds.map(build => {
                        const rrp = calcRrp(build.rrp, buildTotals[build.id] ?? 0);
                        const isCalculated = build.rrp == null || build.rrp <= 0;
                        return (
                          <td key={build.id} className="p-3 text-center text-sm">
                            <span>{formatCurrency(rrp)}</span>
                            {isCalculated && <p className="text-xs font-normal text-muted-foreground">estimated</p>}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {/* EPS rows */}
                  {showEps && comparisonRows.length > 0 && EPS_PLANS.map(plan => (
                    <tr key={plan.months} className="border-b bg-muted/30">
                      <td className="p-3 sticky left-0 z-10 bg-muted/30 font-medium text-sm">
                        EPS x{plan.months}mo
                      </td>
                      {selectedBuilds.map(build => {
                        const rrp = calcRrp(build.rrp, buildTotals[build.id] ?? 0);
                        const monthly = calcEps(rrp, plan.markup, plan.months);
                        return (
                          <td key={build.id} className="p-3 text-center text-sm">
                            <span className="font-semibold">{formatCurrency(monthly)}</span>
                            <span className="text-xs text-muted-foreground">/mo</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {comparisonRows.length === 0 && (
                    <tr>
                      <td colSpan={selectedIds.length + 1} className="p-8 text-center text-muted-foreground">
                        No component data available for selected builds
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 px-4 py-3 border-t text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-amber-100 dark:bg-amber-900/60 border border-amber-400" />
                Major difference (CPU, RAM, Storage, GPU, Case)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-blue-50 dark:bg-blue-900/40 border border-blue-300 dark:border-blue-600" />
                Minor difference (MB, PSU, OS, Accessories)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hidden print-friendly markup */}
      {selectedIds.length >= 2 && (
        <div ref={printRef} className="hidden">
          <table>
            <thead>
              <tr>
                <th>Component</th>
                {selectedBuilds.map(build => (
                  <th key={build.id} className="build-header">
                    {mediaData[build.id] ? (
                      <img src={mediaData[build.id]!.url} alt={build.sku} />
                    ) : (
                      <div className="no-image">No image</div>
                    )}
                    <div className="build-sku">{build.sku}</div>
                    <div className="build-desc">{getDescription(build.id, build.description)}</div>
                    {printOptions.status && <span className="build-status">{build.status}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showApm && hasAnyApm && (
                <tr>
                  <td className="cat-label">APM Score</td>
                  {selectedBuilds.map(build => {
                    const apm = apmData[build.id];
                    return (
                      <td key={build.id} style={{ textAlign: 'center' }}>
                        {apm?.passmark_rating != null ? (
                          <span style={{ fontWeight: 700, fontSize: '13px' }}>{apm.passmark_rating.toLocaleString()}</span>
                        ) : (
                          <span className="em-dash">&mdash;</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              )}
              {showApmParams && hasAnyApm && (
                <>
                  {([
                    { key: 'cpu_mark', label: 'CPU Mark' },
                    { key: 'graphics_3d_mark', label: '3D Graphics' },
                    { key: 'memory_mark', label: 'Memory' },
                    { key: 'disk_mark', label: 'Disk' },
                  ] as { key: string; label: string }[]).map(({ key, label }) => (
                    <tr key={key}>
                      <td className="cat-label" style={{ paddingLeft: '20px', color: '#6b7280', fontWeight: 400 }}>{label}</td>
                      {selectedBuilds.map(build => {
                        const apm = apmData[build.id];
                        const val = apm?.[key as keyof typeof apm] as number | null | undefined;
                        return (
                          <td key={build.id} style={{ textAlign: 'center' }}>
                            {val != null ? val.toLocaleString() : <span className="em-dash">&mdash;</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              )}
              {comparisonRows.filter(row => !hiddenCategories.has(row.cat)).map(row => (
                <tr
                  key={row.cat}
                  className={cn(
                    row.hasDifference && row.isMajor && 'diff-major',
                    row.hasDifference && row.isMinor && 'diff-minor',
                  )}
                >
                  <td className="cat-label">
                    {row.label}
                    {row.hasDifference && row.isMajor && <span className="dot dot-major" />}
                    {row.hasDifference && row.isMinor && <span className="dot dot-minor" />}
                  </td>
                  {row.columns.map((items, colIdx) => {
                    const buildId = selectedIds[colIdx];
                    const visibleItems = items.filter(item => isCompVisible(buildId, item.component_sku));
                    return (
                      <td key={buildId}>
                        {visibleItems.length > 0 ? visibleItems.map((item, i) => (
                          <div key={i} style={{ marginBottom: i < visibleItems.length - 1 ? '4px' : 0 }}>
                            <div className="comp-desc">{getCompDesc(buildId, item.component_sku, item.description || item.component_sku)}</div>
                            {printOptions.codes && <div className="comp-sku">{item.component_sku}</div>}
                            {item.qty_per_build > 1 && <div className="comp-qty">Qty: {item.qty_per_build}</div>}
                            {showSpecs && (() => {
                              const { entries, allMissing } = getSpecEntries(item.component_sku);
                              if (entries.length === 0 || allMissing) return null;
                              return (
                                <div className="spec-line">
                                  {entries.filter(e => !e.missing).map((e, idx, arr) => (
                                    <span key={idx}>
                                      {e.text}{idx < arr.length - 1 ? ' · ' : ''}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        )) : (
                          <span className="em-dash">&mdash;</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {printOptions.price && comparisonRows.length > 0 && (
                <tr className="total-row" style={{ background: '#f0f4ff' }}>
                  <td className="cat-label">RRP (inc. VAT)</td>
                  {selectedBuilds.map(build => {
                    const rrp = calcRrp(build.rrp, buildTotals[build.id] ?? 0);
                    const isCalc = build.rrp == null || build.rrp <= 0;
                    return (
                      <td key={build.id} style={{ textAlign: 'center', fontWeight: 700 }}>
                        {formatCurrency(rrp)}{isCalc ? ' *' : ''}
                      </td>
                    );
                  })}
                </tr>
              )}
              {showEps && printOptions.price && comparisonRows.length > 0 && EPS_PLANS.map(plan => (
                <tr key={plan.months} style={{ background: '#f9fafb' }}>
                  <td className="cat-label">EPS x{plan.months}mo</td>
                  {selectedBuilds.map(build => {
                    const rrp = calcRrp(build.rrp, buildTotals[build.id] ?? 0);
                    const monthly = calcEps(rrp, plan.markup, plan.months);
                    return (
                      <td key={build.id} style={{ textAlign: 'center', fontWeight: 600, fontSize: '11px' }}>
                        {formatCurrency(monthly)}/mo
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="legend">
            <span><span className="legend-swatch" style={{ background: '#fef3c7', border: '1px solid #f59e0b' }} />Major difference (CPU, RAM, Storage, GPU, Case)</span>
            <span><span className="legend-swatch" style={{ background: '#eff6ff', border: '1px solid #93c5fd' }} />Minor difference (MB, PSU, OS, Accessories)</span>
          </div>
        </div>
      )}

      {selectedIds.length < 2 && selectedIds.length > 0 && (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          Add at least one more build to compare
        </div>
      )}

      {selectedIds.length === 0 && (
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
          <ArrowLeftRight className="h-10 w-10 opacity-30" />
          <p>Select builds above to start comparing</p>
        </div>
      )}
    </div>
  );
}
